/**
 * Contratos de los endpoints del maestro de clientes:
 *   - `GET /api/clientes/:id`                → ficha completa de un cliente.
 *   - `GET /api/clientes/listar?id=&nombre=` → filtro del maestro.
 *
 * El backend pega a ACSIS y normaliza los shapes.
 */

import { z } from 'zod';

export const ClienteDomicilioSchema = z.object({
  renglon: z.string().nullable(),
  domicilio: z.string().nullable(),
  calle: z.string().nullable(),
  numeroCalle: z.string().nullable(),
  piso: z.string().nullable(),
  departamento: z.string().nullable(),
  barrio: z.string().nullable(),
  localidad: z.string().nullable(),
  codigoPostal: z.string().nullable(),
  partido: z.string().nullable(),
  provincia: z.string().nullable(),
  pais: z.string().nullable(),
  telefono: z.string().nullable(),
  fax: z.string().nullable(),
  email: z.string().nullable(),
  observacion: z.string().nullable(),
  transporte: z.string().nullable(),
  horarioAtencion: z.string().nullable(),
  horarioEntrega: z.string().nullable(),
  municipio: z.string().nullable(),
  /** GLN destino — ACSIS lo guarda en `barrio` (convención histórica). */
  gln: z.string().nullable(),
  esPrincipal: z.boolean(),
  utilizable: z.boolean(),
});

export const ClienteFlagsSchema = z.object({
  esGlobal: z.boolean(),
  bloqueadoPedidos: z.boolean(),
  bloqueadoFacturas: z.boolean(),
  habilitadoWeb: z.boolean(),
  habilitaConciliacion: z.boolean(),
});

export const ClienteClasificadoresSchema = z.object({
  c1: z.string().nullable(),
  c2: z.string().nullable(),
  c3: z.string().nullable(),
  c4: z.string().nullable(),
  c5: z.string().nullable(),
  c6: z.string().nullable(),
  c7: z.string().nullable(),
  c8: z.string().nullable(),
  c9: z.string().nullable(),
});

export const ClienteAcsisSchema = z.object({
  id: z.string().nullable(),
  nombre: z.string().nullable(),
  nombreLegal: z.string().nullable(),
  cuit: z.string().nullable(),
  condicionIVA: z.string().nullable(),
  ingresosBrutos: z.string().nullable(),
  tipoCliente: z.string().nullable(),
  actividad: z.string().nullable(),
  referencia: z.string().nullable(),
  horarioAtencion: z.string().nullable(),
  horarioEntrega: z.string().nullable(),
  codigoProveedor: z.string().nullable(),
  condicionPago: z.string().nullable(),
  monedaCuentaCorriente: z.string().nullable(),
  cuentaContable: z.string().nullable(),
  contactoVenta: z.string().nullable(),
  contactoCobro: z.string().nullable(),
  vendedor: z.string().nullable(),
  zonaVenta: z.string().nullable(),
  cobrador: z.string().nullable(),
  transporte: z.string().nullable(),
  fechaAlta: z.string().nullable(),
  fechaBaja: z.string().nullable(),
  flags: ClienteFlagsSchema,
  clasificadores: ClienteClasificadoresSchema,
  domicilios: z.array(ClienteDomicilioSchema),
});

export const ClienteBuscarResponse = z.object({
  cliente: ClienteAcsisSchema,
  raw: z.unknown(),
});

export type ClienteAcsis = z.infer<typeof ClienteAcsisSchema>;
export type ClienteDomicilio = z.infer<typeof ClienteDomicilioSchema>;
export type ClienteBuscar = z.infer<typeof ClienteBuscarResponse>;

/**
 * Fila del listado (`GET /api/clientes/listar`). Del array de domicilios el
 * back deja solo los datos del principal (localidad, provincia y GLN destino).
 */
export const ClienteListItemSchema = z.object({
  id: z.string().nullable(),
  nombre: z.string().nullable(),
  nombreLegal: z.string().nullable(),
  cuit: z.string().nullable(),
  tipoCliente: z.string().nullable(),
  condicionIVA: z.string().nullable(),
  localidad: z.string().nullable(),
  provincia: z.string().nullable(),
  /** GLN destino del domicilio principal — ACSIS lo guarda en `barrio`. */
  gln: z.string().nullable(),
  cantidadDomicilios: z.number(),
  /** Sin `fechaBaja` en el ERP. */
  activo: z.boolean(),
  fechaAlta: z.string().nullable(),
});

export const ClienteListarResponse = z.object({
  items: z.array(ClienteListItemSchema),
  total: z.number(),
  truncado: z.boolean(),
  totalMaestro: z.number(),
  sincronizadoEn: z.string().nullable(),
});

export type ClienteListItem = z.infer<typeof ClienteListItemSchema>;
export type ClienteListar = z.infer<typeof ClienteListarResponse>;
